// Plan an event. One plain createEvent mutation — a DB trigger inserts the
// creator as ORGANIZER/CONFIRMED, so no participant row is nested (unlike
// createOuting's owner row). eventRoute links are follow-up mutations, same
// as outing routes. Reached plain ("/event/new"), from a group's events tab
// ("?club=<id>") or from the map's right-click popover ("?lng=&lat=").
import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useSearchParams } from "wouter";
import { X } from "lucide-react";
import { gqlRequest } from "../lib/gql";
import {
  activitiesToWire,
  CREATE_EVENT,
  CREATE_EVENT_ROUTE,
  MY_CLUB_MEMBERSHIPS,
  type CreateEventResult,
  type MyClubMembershipsResult,
} from "../lib/queries";
import { useAuth } from "../lib/auth";
import {
  ACTIVITIES,
  ACTIVITY_ICONS,
  EVENT_JOIN_POLICIES,
  EVENT_RECURRENCES,
  EVENT_VISIBILITIES,
  type Activity,
  type EventJoinPolicy,
  type EventRecurrence,
  type EventVisibility,
} from "../lib/format";
import { useT } from "../lib/i18n";
import { LoginGate } from "../components/LoginGate";
import { PlacePicker, type PickedPlace } from "../components/PlacePicker";
import { RouteLinkPicker, type RouteLink } from "../components/RouteLinkPicker";
import { Chip, Field, Select, TextArea, TextInput } from "../components/ui";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

export function NewEventPage() {
  const [params] = useSearchParams();
  const search = params.toString();
  return (
    <LoginGate action="gateAction.createEvent" next={`/event/new${search ? `?${search}` : ""}`}>
      <EventForm />
    </LoginGate>
  );
}

type Translate = ReturnType<typeof useT>["t"];

function friendlyError(err: unknown, t: Translate): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (/permission|denied|policy/i.test(msg)) return t("err.permission");
  if (/violates|constraint|invalid input/i.test(msg)) return t("err.rejected", { msg });
  return msg;
}

/** Next full hour as a datetime-local input value ("2026-08-02T15:00"). */
function nextHourLocal(): string {
  const d = new Date();
  d.setHours(d.getHours() + 1, 0, 0, 0);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function EventForm() {
  const { personId } = useAuth();
  const { t, labelizeT } = useT();
  const [, navigate] = useLocation();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();

  // ?lng=&lat= from the map popover fixes the event's map point, exactly like
  // NewPlacePage; clearing it re-enables the place picker.
  const paramLng = Number(params.get("lng"));
  const paramLat = Number(params.get("lat"));
  const paramCoords =
    params.has("lng") && params.has("lat") && Number.isFinite(paramLng) && Number.isFinite(paramLat) && Math.abs(paramLat) <= 90
      ? ([paramLng, paramLat] as [number, number])
      : null;

  const memberships = useQuery({
    queryKey: ["my-club-memberships", personId],
    enabled: !!personId,
    queryFn: () => gqlRequest<MyClubMembershipsResult>(MY_CLUB_MEMBERSHIPS, { personId }),
  });
  const myClubs = (memberships.data?.clubMembers.nodes ?? []).filter(
    (m) => m.status === "ACTIVE" && m.club != null,
  );

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [activities, setActivities] = useState<Activity[]>([]);
  const [startsAt, setStartsAt] = useState(nextHourLocal);
  const [endsAt, setEndsAt] = useState("");
  const [joinPolicy, setJoinPolicy] = useState<EventJoinPolicy>("OPEN");
  const [visibility, setVisibility] = useState<EventVisibility>("PUBLIC");
  const [recurrence, setRecurrence] = useState<EventRecurrence>("NONE");
  const [recurrenceUntil, setRecurrenceUntil] = useState("");
  const [maxParticipants, setMaxParticipants] = useState("");
  const [clubId, setClubId] = useState(() => params.get("club") ?? "");
  const [coords, setCoords] = useState<[number, number] | null>(paramCoords);
  const [place, setPlace] = useState<PickedPlace | null>(null);
  const [showOnMap, setShowOnMap] = useState(true);
  const [routeLinks, setRouteLinks] = useState<RouteLink[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // MEMBERS visibility and MEMBERS_ONLY policy require a club (DB check
  // constraints) — deselecting the club falls back to safe values.
  function onClubChange(next: string) {
    setClubId(next);
    if (!next) {
      if (visibility === "MEMBERS") setVisibility("PUBLIC");
      if (joinPolicy === "MEMBERS_ONLY") setJoinPolicy("OPEN");
    }
  }
  const visibilityOptions = EVENT_VISIBILITIES.filter((v) => clubId || v !== "MEMBERS");
  const joinPolicyOptions = EVENT_JOIN_POLICIES.filter((p) => clubId || p !== "MEMBERS_ONLY");

  const create = useMutation({
    mutationFn: async () => {
      const input = {
        title: title.trim(),
        description: description.trim() || null,
        // Enum ARRAYS go lowercase on the wire (see TODO(pdbq) in queries.ts).
        activities: activities.length ? activitiesToWire(activities) : null,
        clubId: clubId || null,
        placeId: place?.id ?? null,
        // GeoJSON object form — same scalar behaviour as createPlace.
        location: coords ? { type: "Point", coordinates: coords } : null,
        startsAt: new Date(startsAt).toISOString(),
        endsAt: endsAt ? new Date(endsAt).toISOString() : null,
        joinPolicy,
        visibility,
        recurrence,
        recurrenceUntil: recurrence !== "NONE" && recurrenceUntil ? recurrenceUntil : null,
        maxParticipants: maxParticipants ? Math.round(Number(maxParticipants)) : null,
        showOnMap,
        // RLS insists this matches the JWT claim; nothing defaults it. The
        // organizer participant row comes from a DB trigger.
        createdBy: personId,
      };
      const result = await gqlRequest<CreateEventResult>(CREATE_EVENT, { input });
      const eventId = result.createEvent.event.id;
      // List order becomes the sort value, like outing routes.
      for (const [sort, link] of routeLinks.entries()) {
        await gqlRequest(CREATE_EVENT_ROUTE, { eventId, routeId: link.route.id, sort });
      }
      return eventId;
    },
    // onSettled, not onSuccess: the event may exist even when a follow-up
    // route mutation failed, so caches must refetch either way.
    onSettled: () => {
      if (clubId) {
        queryClient.invalidateQueries({ queryKey: ["club", clubId] });
        queryClient.invalidateQueries({ queryKey: ["club-events", clubId] });
      }
      queryClient.invalidateQueries({ queryKey: ["my-event-participations"] });
    },
    onSuccess: (eventId) => {
      navigate(`/event/${eventId}`);
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!title.trim()) errors.title = t("newEvent.errTitle");
    if (!startsAt) errors.startsAt = t("newEvent.errStart");
    if (endsAt && startsAt && new Date(endsAt) <= new Date(startsAt)) errors.endsAt = t("newEvent.errEnd");
    if (maxParticipants && (!Number.isFinite(Number(maxParticipants)) || Number(maxParticipants) <= 0))
      errors.maxParticipants = t("newEvent.errCap");
    setFieldErrors(errors);
    if (Object.keys(errors).length === 0) create.mutate();
  }

  return (
    <div className="mx-auto max-w-xl p-4">
      <h1 className="mb-5 text-2xl font-semibold tracking-tight text-foreground">{t("newEvent.title")}</h1>
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label={t("form.title")} error={fieldErrors.title}>
          <TextInput value={title} placeholder={t("newEvent.titlePlaceholder")} onChange={(e) => setTitle(e.target.value)} />
        </Field>

        <Field label={t("form.description")}>
          <TextArea
            rows={4}
            value={description}
            placeholder={t("newEvent.descriptionPlaceholder")}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>

        <Field label={t("form.activities")}>
          <div className="flex flex-wrap gap-2">
            {ACTIVITIES.map((a) => {
              const Icon = ACTIVITY_ICONS[a];
              const active = activities.includes(a);
              return (
                <Chip
                  key={a}
                  active={active}
                  onClick={() => setActivities(active ? activities.filter((x) => x !== a) : [...activities, a])}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Icon aria-hidden className="size-3.5" />
                    {labelizeT(a)}
                  </span>
                </Chip>
              );
            })}
          </div>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t("newEvent.start")} error={fieldErrors.startsAt}>
            <TextInput type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
          </Field>
          <Field label={t("newEvent.endOptional")} error={fieldErrors.endsAt}>
            <TextInput type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
          </Field>
        </div>

        <Field label={t("newEvent.groupOptional")}>
          <Select value={clubId} onChange={(e) => onClubChange(e.target.value)}>
            <option value="">{t("newEvent.noGroup")}</option>
            {myClubs.map((m) => (
              <option key={m.clubId} value={m.clubId}>
                {m.club!.name}
              </option>
            ))}
          </Select>
          <p className="mt-1 text-xs text-muted-foreground">{t("newEvent.groupHint")}</p>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t("group.joinPolicy")}>
            <Select value={joinPolicy} onChange={(e) => setJoinPolicy(e.target.value as EventJoinPolicy)}>
              {joinPolicyOptions.map((p) => (
                <option key={p} value={p}>
                  {labelizeT(p)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("form.visibility")}>
            <Select value={visibility} onChange={(e) => setVisibility(e.target.value as EventVisibility)}>
              {visibilityOptions.map((v) => (
                <option key={v} value={v}>
                  {labelizeT(v)}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t("event.recurrence")}>
            <Select value={recurrence} onChange={(e) => setRecurrence(e.target.value as EventRecurrence)}>
              {EVENT_RECURRENCES.map((r) => (
                <option key={r} value={r}>
                  {labelizeT(r)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("newEvent.maxOptional")} error={fieldErrors.maxParticipants}>
            <TextInput
              type="number"
              inputMode="numeric"
              min={1}
              value={maxParticipants}
              placeholder="e.g. 12"
              onChange={(e) => setMaxParticipants(e.target.value)}
            />
          </Field>
        </div>
        {recurrence !== "NONE" && (
          <Field label={t("newEvent.repeatsUntil")}>
            <TextInput type="date" value={recurrenceUntil} onChange={(e) => setRecurrenceUntil(e.target.value)} />
          </Field>
        )}

        <Field label={t("newEvent.meetingPointOptional")}>
          {coords ? (
            <div className="flex items-center justify-between rounded-xl border bg-accent px-3 py-2">
              <span className="text-sm text-foreground">
                {t("newEvent.mapPoint", { lat: coords[1].toFixed(5), lng: coords[0].toFixed(5) })}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setCoords(null)}
                className="ml-2 shrink-0 text-muted-foreground hover:text-destructive"
                aria-label={t("newEvent.clearPointAria")}
              >
                <X aria-hidden className="size-4" />
              </Button>
            </div>
          ) : (
            <PlacePicker selected={place ? [place] : []} onChange={(next) => setPlace(next[next.length - 1] ?? null)} />
          )}
        </Field>

        <label className="flex items-center gap-2 text-sm text-foreground">
          <Checkbox checked={showOnMap} onCheckedChange={(checked) => setShowOnMap(checked === true)} />
          {t("newEvent.showOnMap")}
        </label>
        <p className="text-xs text-muted-foreground">{t("newEvent.showOnMapHint")}</p>

        <Field label={t("newEvent.routesOptional")}>
          <RouteLinkPicker simple links={routeLinks} onChange={setRouteLinks} />
        </Field>

        <p className="text-xs text-muted-foreground">{t("newEvent.organizerNote")}</p>

        {create.error != null && (
          <Alert variant="destructive">
            <AlertDescription>{friendlyError(create.error, t)}</AlertDescription>
          </Alert>
        )}

        <Button type="submit" disabled={create.isPending} className="w-full py-2.5">
          {create.isPending ? t("common.saving") : t("newEvent.submit")}
        </Button>
      </form>
    </div>
  );
}
